<template>
  <div class="p-4">
    <div class="row resourceData-wrapper">
      <h5 class="mb-3">{{ $t("resource-log-information") }} </h5>
      <section>
        <div class="client-general-information__data">
          <ul class="row m-0 p-0">
            <li class="col-12 mb-2">
              <h4>{{ $t("general-information") }}</h4>
            </li>
            <li class="col-12 col-sm-6 col-md-4 col-xl-3">
              <h5>{{ $t("file-name") }}</h5>
              <p>{{ resourceData?.fileName || "-" }}</p>
            </li>

            <li class="col-12 col-sm-6 col-md-4 col-xl-3">
              <h5>{{ $t("type") }}</h5>
              <p>{{ resourceData?.resourceType ? $t(resourceData.resourceType.toLowerCase()).toUpperCase() : "-" }}</p>
            </li>

            <li class="col-12 col-sm-6 col-md-4 col-xl-3">
              <h5>{{ $t("status") }}</h5>
              <p>{{ resourceData?.status ? $t(resourceData.status.toLowerCase().replace("_", "-")).toUpperCase() : "-" }}</p>
            </li>
            <li class="col-12 col-sm-6 col-md-4 col-xl-3">
              <h5>{{ $t("created-by") }}</h5>
              <p>{{ resourceData?.created_by }}</p>
            </li>
            <li class="col-12 col-sm-6 col-md-4 col-xl-3">
              <h5>{{ $t("upload-time-ksa") }}</h5>
              <p>{{ formatDateKSA(resourceData?.created_on) }}</p>
            </li>
            <li class="col-12 col-sm-6 col-md-4 col-xl-3">
              <h5>{{ $t("download") }}</h5>
              <a v-if="resourceData?.fileUrl" :href="resourceData.fileUrl" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-outline-primary">{{ $t("download") }}</a>
              <p v-else>-</p>
            </li>
            <li class="col-12 col-md-6 col-xl-6">
              <h5>{{ $t("processed") }}</h5>
              <b-progress :max="resourceData?.totalRecords" >
                <b-progress-bar :value="resourceData?.processed" >
                  <span><strong>{{ resourceData?.processed }} / {{ resourceData?.totalRecords }}</strong></span>
                </b-progress-bar>
              </b-progress>
            </li>
            <li class="col-12 col-sm-6 col-md-4 col-xl-3">
              <h5>{{ $t("failed-imports") }}</h5>
              <p>{{ resourceData?.failedImports }}</p>
            </li>
            <li class="col-12 col-sm-6 col-md-4 col-xl-3">
              <h5>{{ $t("successful-imports") }}</h5>
              <p>{{ resourceData?.successfulImports }}</p>
            </li>
            <li class="col-12 col-sm-6 col-md-4 col-xl-3">
              <h5>{{ $t("new-resources") }}</h5>
              <p>{{ resourceData?.newResources }}</p>
            </li>
            <li class="col-12 col-sm-6 col-md-4 col-xl-3">
              <h5>{{ $t("updated-resources") }}</h5>
              <p>{{ resourceData?.updatedResources }}</p>
            </li>
          </ul>
        </div>
      </section>

      <section v-if="resourceData.failureDetails && resourceData.failureDetails.length">
        <div class="client-general-information__data">
          <ul class="row m-0 p-0">
            <li class="col-12 mb-2">
              <h4>{{ $t("failure-details") }}</h4>
            </li>
          </ul>
          <b-table :items="resourceData.failureDetails" :fields="fields" show-empty responsive="lg"
            :thead-class="`table-thead text-center bg-transparent`" :tbody-class="`table-tbody text-center`"
            ref="failureDetailsTable" class="new-theme-custom-table"
            :per-page="fdPerPage" :current-page="fdCurrentPage">
            <template #empty> {{ $t("empty-row") }} </template>
          </b-table>
          <b-pagination v-if="resourceData.failureDetails && resourceData.failureDetails.length > fdPerPage"
            v-model="fdCurrentPage"
            :total-rows="resourceData.failureDetails.length"
            :per-page="fdPerPage"
            first-number
            last-number
            aria-controls="failureDetailsTable"
            class="d-flex justify-content-end mt-3"
          />
        </div>
      </section>
      <section v-if="resourceData.successDetails && resourceData.successDetails.length">
        <div class="client-general-information__data">
          <ul class="row m-0 p-0">
            <li class="col-12 mb-2">
              <h4>{{ $t("successful-items") }}</h4>
            </li>
          </ul>
          <b-table :items="resourceData.successDetails" :fields="successDetailsFields" show-empty responsive="lg"
            :thead-class="`table-thead text-center bg-transparent`" :tbody-class="`table-tbody text-center`"
            ref="successDetailsTable" class="new-theme-custom-table"
            :per-page="sdPerPage" :current-page="sdCurrentPage">
            <template #cell(action)="{ item }">
              {{ item.action === 'created' ? $t('created') : $t('updated') }}
            </template>
            <template #empty> {{ $t("empty-row") }} </template>
          </b-table>
          <b-pagination v-if="resourceData.successDetails && resourceData.successDetails.length > sdPerPage"
            v-model="sdCurrentPage"
            :total-rows="resourceData.successDetails.length"
            :per-page="sdPerPage"
            first-number
            last-number
            aria-controls="successDetailsTable"
            class="d-flex justify-content-end mt-3"
          />
        </div>
      </section>
      <section v-if="resourceData.successWithWarnings && resourceData.successWithWarnings.length">
        <div class="client-general-information__data">
          <ul class="row m-0 p-0">
            <li class="col-12 mb-2">
              <h4>{{ $t("success-with-warnings") }}</h4>
            </li>
          </ul>
          <b-table :items="resourceData.successWithWarnings" :fields="fields" show-empty responsive="lg"
            :thead-class="`table-thead text-center bg-transparent`" :tbody-class="`table-tbody text-center`"
            ref="successWithWarningsTable" class="new-theme-custom-table"
            :per-page="swPerPage" :current-page="swCurrentPage">
            <template #empty> {{ $t("empty-row") }} </template>
          </b-table>
          <b-pagination v-if="resourceData.successWithWarnings && resourceData.successWithWarnings.length > swPerPage"
            v-model="swCurrentPage"
            :total-rows="resourceData.successWithWarnings.length"
            :per-page="swPerPage"
            first-number
            last-number
            aria-controls="successWithWarningsTable"
            class="d-flex justify-content-end mt-3"
          />
        </div>
      </section>
      <section v-if="resourceData.updateLogs && resourceData.updateLogs.length">
        <div class="client-general-information__data">
          <ul class="row m-0 p-0">
            <li class="col-12 mb-2">
              <h4>{{ $t("update-logs") }}</h4>
            </li>
          </ul>
          <b-table :items="resourceData.updateLogs" :fields="updateFields" show-empty responsive="lg"
            :thead-class="`table-thead text-center bg-transparent`" :tbody-class="`table-tbody text-center`"
            ref="updateLogsTable" class="new-theme-custom-table"
            :per-page="ulPerPage" :current-page="ulCurrentPage">
            <template #cell(id)="{ item }">
              <div>{{ item.id || item._id || '-' }}</div>
            </template>

            <template #cell(changes)="{ item }">
              <div class="json-container">
                <pre class="json-pre">{{ prettyJson(item.changes) }}</pre>
              </div>
            </template>

            <template #empty> {{ $t("empty-row") }} </template>
          </b-table>
          <b-pagination v-if="resourceData.updateLogs && resourceData.updateLogs.length > ulPerPage"
            v-model="ulCurrentPage"
            :total-rows="resourceData.updateLogs.length"
            :per-page="ulPerPage"
            first-number
            last-number
            aria-controls="updateLogsTable"
            class="d-flex justify-content-end mt-3"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import moment from "moment"

export default {
  name: "ResourceLogDetails",
  props: ["resourceData"],
  data() {
    return {
      fdPerPage: 3,
      fdCurrentPage: 1,
      ulPerPage: 3,
      ulCurrentPage: 1,
      swPerPage: 2,
      swCurrentPage: 1,
      sdPerPage: 5,
      sdCurrentPage: 1,
    };
  },
  computed: {
    fields() {
      return [
        { key: "identifier", label: this.$t("id"), sortable: false },
        { key: "reasons", label: this.$t("reasons"), sortable: false },
      ];
    },
    updateFields() {
      return [
        { key: "identifier", label: this.$t("id"), sortable: false },
        { key: "changes", label: this.$t("changes"), sortable: false },
      ];
    },
    successDetailsFields() {
      return [
        { key: "identifier", label: this.$t("id"), sortable: false },
        { key: "action", label: this.$t("action"), sortable: false },
      ];
    },
  },
  methods: {
    formatDate(date) {
      return moment(date).locale("en").format("DD-MM-YYYY HH:mm");
    },
    formatDateKSA(date) {
      if (!date) return "-";
      return moment.utc(date).utcOffset(3).locale("en").format("DD-MM-YYYY HH:mm");
    },
    prettyJson(payload) {
      if (!payload && payload !== 0) return '-';
      try {
        let obj = payload;
        if (typeof payload === 'string') {
          obj = JSON.parse(payload);
        }
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        // If parsing fails, return the original string truncated
        return String(payload);
      }
    },
  },
  i18n: {
    messages: {
      en: require("@/services/i18n/locales/en/manageTrips.json"),
      ar: require("@/services/i18n/locales/ar/manageTrips.json"),
    },
  },
};
</script>

<style lang="scss" scoped>
.client-general-information {
  &__description {
    span {
      font-weight: 700;
      font-size: 14px;
      line-height: 35px;
      letter-spacing: 0.44px;
      margin-inline-start: 0.5rem;
    }
  }

  &__action {
    img {
      cursor: pointer;
    }
  }

  &__data {
    background-color: var(--color-01);
    border-radius: 4px;
    padding-inline: 3.3125rem;
    padding-block: 2.3125rem;
    margin-block-start: 0.625rem;
    margin-block-end: 1rem;
    border: 1px solid var(--color-14);
    box-shadow: 0 2px 3px 0 var(--color-38);

    ul {
      li {
        list-style: none;

        h5,
        h4 {
          color: var(--color-02);
          font-size: 14px;
          line-height: 20px;
          letter-spacing: 0.25px;
          line-height: 20px;
        }

        h4 {
          color: var(--color-03);
          font-size: 15px;
          margin-inline-start: -10px;
        }

        p {
          color: var(--color-17);
          font-size: 14px;
          line-height: 20px;
          letter-spacing: 0.25px;
          font-weight: 700;
          line-height: 20px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }
}

@media (max-width: 576px) {
  .client-general-information {
    &__data {
      text-align: center;
      padding-inline: 0.9375rem;
    }
  }
}

.json-container {
  max-height: 180px;
  overflow: auto;
  text-align: left; /* We'll handle text direction with 'dir' attribute */
  direction: ltr; /* Default fallback */
}
.json-pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: Menlo, Monaco, 'Courier New', monospace;
  font-size: 12px;
  margin: 0;
  direction: ltr; /* Always show JSON LTR even in RTL layouts */
  text-align: left;
}
</style>